/**
 * Szerver oldali hiba részletek kinyerése axios hibából.
 *
 * Az axios `error.message` csak annyit mond: "Request failed with status code 500".
 * A tényleges ok a válasz törzsében van (Laravel: message / exception / file / line / trace),
 * ezt szedjük ki itt strukturáltan, hogy a szinkron naplóban visszanézhető legyen.
 */

const MAX_TRACE_LENGTH = 4000;
const MAX_BODY_LENGTH = 4000;

function safeStringify(value, maxLength) {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "string") {
    return value.slice(0, maxLength);
  }

  try {
    return JSON.stringify(value, null, 2).slice(0, maxLength);
  } catch (e) {
    return String(value).slice(0, maxLength);
  }
}

function normalizeTrace(trace) {
  if (typeof trace === "string") {
    return trace.slice(0, MAX_TRACE_LENGTH);
  }

  if (!Array.isArray(trace)) {
    return "";
  }

  return trace
    .slice(0, 25)
    .map((frame, index) => {
      if (typeof frame === "string") {
        return `#${index} ${frame}`;
      }

      const location = [frame?.file, frame?.line].filter(Boolean).join(":");
      const call = [frame?.class, frame?.type, frame?.function].filter(Boolean).join("");

      return `#${index} ${location}${call ? ` ${call}()` : ""}`;
    })
    .join("\n")
    .slice(0, MAX_TRACE_LENGTH);
}

function extractFromHtml(html) {
  if (typeof html !== "string" || !/<\s*(html|body|!doctype)/i.test(html)) {
    return "";
  }

  const patterns = [
    /<title[^>]*>([\s\S]*?)<\/title>/i,
    /class="[^"]*exception_title[^"]*"[^>]*>([\s\S]*?)</i,
    /class="[^"]*exception-message[^"]*"[^>]*>([\s\S]*?)</i,
  ];

  for (const pattern of patterns) {
    const match = html.match(pattern);

    if (match && match[1]) {
      const text = match[1]
        .replace(/<[^>]+>/g, " ")
        .replace(/&nbsp;/g, " ")
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/\s+/g, " ")
        .trim();

      if (text) {
        return text;
      }
    }
  }

  return "";
}

function collectValidationMessages(errors) {
  if (!errors || typeof errors !== "object") {
    return [];
  }

  return Object.entries(errors)
    .map(([field, messages]) => {
      const list = Array.isArray(messages) ? messages.flat() : [messages];
      const text = list.filter(Boolean).join(" | ");

      return text ? `${field}: ${text}` : "";
    })
    .filter(Boolean);
}

/**
 * @returns {{
 *   message: String,
 *   status: Number|null,
 *   statusText: String,
 *   requestUrl: String,
 *   requestMethod: String,
 *   serverMessage: String,
 *   serverException: String,
 *   serverFile: String,
 *   serverLine: Number|null,
 *   serverTrace: String,
 *   responseBody: String,
 * }}
 */
export function extractServerErrorDetails(error) {
  const response = error?.response;
  const request = error?.request;
  const config = error?.config || response?.config;

  // A törzs több formában érkezhet: objektum, string, vagy üres (blob/XHR esetén
  // csak a nyers responseText áll rendelkezésre).
  let data = response?.data;

  if ((data === undefined || data === null || data === "") && request) {
    const rawText = request.responseText || "";

    if (rawText) {
      try {
        data = JSON.parse(rawText);
      } catch (e) {
        data = rawText;
      }
    }
  }

  const validationMessages = collectValidationMessages(data?.errors);
  const errorBag = data?.errors?.error;
  const bagMessages = Array.isArray(errorBag)
    ? errorBag.flat().filter(Boolean)
    : errorBag
      ? [errorBag]
      : [];

  const serverMessage =
    bagMessages.join(" | ") ||
    data?.message ||
    data?.error ||
    extractFromHtml(data) ||
    (typeof data === "string" ? data.slice(0, 2000) : "") ||
    validationMessages.join(" | ");

  const serverLine = Number.isFinite(Number(data?.line)) ? Number(data.line) : null;
  const status = response?.status || request?.status || null;
  const hasBody = data !== undefined && data !== null && data !== "";

  return {
    message: serverMessage || error?.message || "",
    status,
    statusText: response?.statusText || request?.statusText || "",
    requestUrl: config?.url || request?.responseURL || "",
    requestMethod: (config?.method || "").toUpperCase(),
    serverMessage: serverMessage || "",
    serverException: data?.exception || "",
    serverFile: data?.file || "",
    serverLine,
    serverTrace: normalizeTrace(data?.trace),
    responseBody: safeStringify(data, MAX_BODY_LENGTH),
    hasResponseBody: hasBody,
  };
}

/**
 * Emberi olvasásra szánt, több soros hibaszöveg a naplóhoz.
 */
export function formatServerErrorForLog(error, fallbackMessage = "") {
  const details = extractServerErrorDetails(error);
  const lines = [];

  const head = [details.status ? `HTTP ${details.status}` : "", details.statusText]
    .filter(Boolean)
    .join(" ");

  if (head) {
    lines.push(head);
  }

  if (details.requestUrl) {
    lines.push(`${details.requestMethod} ${details.requestUrl}`.trim());
  }

  lines.push(details.serverMessage || error?.message || fallbackMessage || "Unknown error");

  if (details.serverException) {
    lines.push(`Exception: ${details.serverException}`);
  }

  if (details.serverFile) {
    lines.push(`At: ${details.serverFile}${details.serverLine ? `:${details.serverLine}` : ""}`);
  }

  if (details.serverTrace) {
    lines.push("Trace:");
    lines.push(details.serverTrace);
  }

  // A nyers választ mindig megmutatjuk, ha az nem csak a már kiírt üzenetet tartalmazza.
  if (details.responseBody && !details.serverTrace) {
    const bodyIsRedundant =
      details.responseBody.trim() === (details.serverMessage || "").trim() ||
      details.responseBody.trim() === `"${(details.serverMessage || "").trim()}"`;

    if (!bodyIsRedundant) {
      lines.push("Response body:");
      lines.push(details.responseBody);
    }
  }

  if (!details.hasResponseBody) {
    lines.push(
      "Response body: <empty> — the server returned no error details. " +
        "Enable APP_DEBUG or return the exception in the JSON response to see the server-side stack trace."
    );
  }

  return { text: lines.filter(Boolean).join("\n"), details };
}
